/**
 * F10 — Admin venue create page (multi-step Stepper flow).
 *
 * Route: /venues/new
 * Permission: venues:write
 *
 * Steps:
 *  1. Basics — name, description, phone, whatsappPhone, website + social_links
 *  2. Location — governorate, caza, city, street_address, lat+lng via MapPicker
 *  3. Sports — multi-select sport chips, one marked primary
 *  4. Hours — DayTimeBandSelector
 *  5. Photos — ImageUploader + S3 presigned PUT + attachPhoto
 *  6. Review — summary + submit
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useI18n } from '@bravemove/i18n/react';
import { SportIcon } from '@bravemove/design-tokens/icons';
import {
  Banner,
  Button,
  Card,
  Chip,
  DayTimeBandSelector,
  type DayTimeBandSelectorValue,
  FormField,
  ImageUploader,
  type UploadedImage,
  OsmMapPicker,
  Select,
  Spinner,
  Stepper,
  Textarea,
  TextInput,
  useToast,
} from '@bravemove/ui-web';
import {
  SPORT_NAME_VALUES,
  type SportName,
  type VenueOpeningHours,
  type VenueOpeningInterval,
} from '@bravemove/types';
import { adminVenueApi } from '../api/venue.js';
import { getGeo } from '../api/geo';
import { translateApiError } from '../auth/errors.js';
import { PageHeader } from '../components/PageHeader';

const STEPS = ['basics', 'location', 'sports', 'hours', 'photos', 'review'] as const;
type Step = (typeof STEPS)[number];

const STEP_INDEX: Record<Step, number> = {
  basics: 0,
  location: 1,
  sports: 2,
  hours: 3,
  photos: 4,
  review: 5,
};

// DayTimeBandSelector uses a Set-based value, not a week-hours map.
// We use it as an availability selector (days + time bands), which maps to
// opening hours on submission (see `buildOpeningHours`).

// ─── Geo (F04) helpers — cascading governorate → caza → city ────────────────

interface GeoOption {
  readonly value: string;
  readonly label: string;
}

/** Time ranges behind the DayTimeBandSelector bands (mirrors BAND_LABELS). */
const BAND_RANGES = {
  morning: { open: '06:00', close: '12:00' },
  afternoon: { open: '12:00', close: '18:00' },
  evening: { open: '18:00', close: '22:00' },
  night: { open: '22:00', close: '06:00' },
} as const;

/**
 * Convert the selected time bands into opening intervals. Contiguous day
 * bands (morning/afternoon/evening) merge into one interval; the overnight
 * night band always stays its own `22:00–06:00` interval.
 */
function bandsToIntervals(bands: DayTimeBandSelectorValue['bands']): VenueOpeningInterval[] {
  const chain = ['morning', 'afternoon', 'evening'] as const;
  const intervals: Array<{ open: string; close: string }> = [];
  let current: { open: string; close: string } | null = null;
  for (const band of chain) {
    if (!bands.has(band)) {
      if (current) {
        intervals.push(current);
        current = null;
      }
      continue;
    }
    current = current
      ? { open: current.open, close: BAND_RANGES[band].close }
      : { ...BAND_RANGES[band] };
  }
  if (current) intervals.push(current);
  if (bands.has('night')) intervals.push({ ...BAND_RANGES.night });
  return intervals;
}

/**
 * Map the wizard's days × bands selection onto the `VenueOpeningHours` week
 * shape (`[]` = closed that day). Returns undefined when nothing was selected
 * so the field is simply omitted from the create payload.
 */
function buildOpeningHours(
  hours: DayTimeBandSelectorValue,
): Record<keyof VenueOpeningHours, VenueOpeningInterval[]> | undefined {
  if (hours.days.size === 0 || hours.bands.size === 0) return undefined;
  const intervals = bandsToIntervals(hours.bands);
  const forDay = (d: 'mon' | 'tue' | 'wed' | 'thu' | 'fri' | 'sat' | 'sun') =>
    hours.days.has(d) ? intervals : [];
  return {
    mon: forDay('mon'),
    tue: forDay('tue'),
    wed: forDay('wed'),
    thu: forDay('thu'),
    fri: forDay('fri'),
    sat: forDay('sat'),
    sun: forDay('sun'),
  };
}

// ─── Form schemas per step ──────────────────────────────────────────────────

const BasicsSchema = z.object({
  name: z.string().min(3, 'At least 3 chars').max(120, 'Max 120 chars'),
  description: z.string().max(500, 'Max 500 chars').optional(),
  phone: z.string().min(1, 'Required').max(20),
  whatsappPhone: z.string().max(20).optional(),
  website: z.string().url('Must be a URL').optional().or(z.literal('')),
  instagram: z.string().url('Must be a URL').optional().or(z.literal('')),
  facebook: z.string().url('Must be a URL').optional().or(z.literal('')),
  tiktok: z.string().url('Must be a URL').optional().or(z.literal('')),
});
type BasicsValues = z.infer<typeof BasicsSchema>;

const LocationSchema = z.object({
  governorateIso: z.string().min(1, 'Required'),
  cazaCode: z.string().min(1, 'Required'),
  cityCode: z.string().min(1, 'Required'),
  streetAddress: z.string().max(240).optional(),
  lat: z.number({ invalid_type_error: 'Required' }).min(-90).max(90),
  lng: z.number({ invalid_type_error: 'Required' }).min(-180).max(180),
});
type LocationValues = z.infer<typeof LocationSchema>;

const _SportsSchema = z.object({
  sports: z.array(z.string()).min(1, 'At least one sport'),
  primarySport: z.string().optional(),
});
type SportsValues = z.infer<typeof _SportsSchema>;

// ─── Aggregated form state ───────────────────────────────────────────────────

interface FormState {
  basics: BasicsValues;
  location: LocationValues;
  sports: SportsValues;
  hours: DayTimeBandSelectorValue;
  photos: ReadonlyArray<UploadedImage>;
}

const EMPTY_BASICS: BasicsValues = {
  name: '',
  description: '',
  phone: '',
  whatsappPhone: '',
  website: '',
  instagram: '',
  facebook: '',
  tiktok: '',
};

const EMPTY_LOCATION: LocationValues = {
  governorateIso: '',
  cazaCode: '',
  cityCode: '',
  streetAddress: '',
  lat: 33.888,
  lng: 35.495,
};

const EMPTY_SPORTS: SportsValues = { sports: [], primarySport: undefined };

const EMPTY_HOURS: DayTimeBandSelectorValue = {
  days: new Set(),
  bands: new Set(),
};

// ─── Sub-step components ─────────────────────────────────────────────────────

function BasicsStep({
  defaultValues,
  onNext,
}: {
  defaultValues: BasicsValues;
  onNext: (values: BasicsValues) => void;
}) {
  const { t } = useI18n();
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<BasicsValues>({
    resolver: zodResolver(BasicsSchema),
    defaultValues,
  });

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        void handleSubmit(onNext)(e);
      }}
      className="space-y-4"
    >
      <FormField label={t('admin:venue.fields.name')} error={errors.name?.message} required>
        <TextInput placeholder={t('admin:venue.fields.namePlaceholder')} {...register('name')} />
      </FormField>
      <FormField label={t('admin:venue.fields.description')} error={errors.description?.message}>
        <Textarea
          rows={3}
          maxLength={500}
          showCounter
          placeholder={t('admin:venue.fields.descriptionPlaceholder')}
          {...register('description')}
        />
      </FormField>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <FormField label={t('admin:venue.fields.phone')} error={errors.phone?.message} required>
          <TextInput
            placeholder={t('admin:venue.fields.phonePlaceholder')}
            {...register('phone')}
          />
        </FormField>
        <FormField
          label={t('admin:venue.fields.whatsappPhone')}
          error={errors.whatsappPhone?.message}
        >
          <TextInput
            placeholder={t('admin:venue.fields.whatsappPhonePlaceholder')}
            {...register('whatsappPhone')}
          />
        </FormField>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <FormField label={t('admin:venue.fields.website')} error={errors.website?.message}>
          <TextInput
            placeholder={t('admin:venue.fields.websitePlaceholder')}
            {...register('website')}
          />
        </FormField>
        <FormField label={t('admin:venue.fields.instagram')} error={errors.instagram?.message}>
          <TextInput
            placeholder={t('admin:venue.fields.instagramPlaceholder')}
            {...register('instagram')}
          />
        </FormField>
        <FormField label={t('admin:venue.fields.facebook')} error={errors.facebook?.message}>
          <TextInput
            placeholder={t('admin:venue.fields.facebookPlaceholder')}
            {...register('facebook')}
          />
        </FormField>
        <FormField label={t('admin:venue.fields.tiktok')} error={errors.tiktok?.message}>
          <TextInput
            placeholder={t('admin:venue.fields.tiktokPlaceholder')}
            {...register('tiktok')}
          />
        </FormField>
      </div>
      <div className="flex justify-end pt-2">
        <Button type="submit" variant="primary">
          {t('admin:common.next')}
        </Button>
      </div>
    </form>
  );
}

function LocationStep({
  defaultValues,
  onNext,
  onBack,
}: {
  defaultValues: LocationValues;
  onNext: (values: LocationValues) => void;
  onBack: () => void;
}) {
  const { t } = useI18n();
  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm<LocationValues>({
    resolver: zodResolver(LocationSchema),
    defaultValues,
  });

  // ── F04 geo data — cascading governorate → caza → city (V-01 fix) ────────
  const governorateIso = watch('governorateIso');
  const cazaCode = watch('cazaCode');
  const [governorates, setGovernorates] = useState<readonly GeoOption[]>([]);
  const [cazas, setCazas] = useState<readonly GeoOption[]>([]);
  const [cities, setCities] = useState<readonly GeoOption[]>([]);
  const [geoError, setGeoError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    getGeo<{ data: ReadonlyArray<{ isoCode: string; name: string }> }>('/geo/governorates')
      .then((res) => {
        if (cancelled) return;
        setGovernorates(res.data.map((g) => ({ value: g.isoCode, label: g.name })));
      })
      .catch(() => {
        if (!cancelled) setGeoError(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!governorateIso) {
      setCazas([]);
      return undefined;
    }
    let cancelled = false;
    getGeo<{ data: ReadonlyArray<{ code: string; name: string }> }>(
      `/geo/cazas?governorateCode=${encodeURIComponent(governorateIso)}`,
    )
      .then((res) => {
        if (cancelled) return;
        setCazas(res.data.map((c) => ({ value: c.code, label: c.name })));
      })
      .catch(() => {
        if (!cancelled) setGeoError(true);
      });
    return () => {
      cancelled = true;
    };
  }, [governorateIso]);

  useEffect(() => {
    if (!cazaCode) {
      setCities([]);
      return undefined;
    }
    let cancelled = false;
    getGeo<{ data: ReadonlyArray<{ code: string; name: string }> }>(
      `/geo/cities?cazaCode=${encodeURIComponent(cazaCode)}`,
    )
      .then((res) => {
        if (cancelled) return;
        setCities(res.data.map((c) => ({ value: c.code, label: c.name })));
      })
      .catch(() => {
        if (!cancelled) setGeoError(true);
      });
    return () => {
      cancelled = true;
    };
  }, [cazaCode]);

  const govReg = register('governorateIso');
  const cazaReg = register('cazaCode');
  const placeholder = (key: string, def: string) => t(key, { defaultValue: def });

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        void handleSubmit(onNext)(e);
      }}
      className="space-y-4"
    >
      {geoError ? (
        <Banner tone="danger">
          {t('admin:venue.create.geoLoadError', {
            defaultValue: 'Regions could not be loaded. Reload the page and try again.',
          })}
        </Banner>
      ) : null}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <FormField
          label={t('admin:venue.fields.governorate')}
          error={errors.governorateIso?.message}
          required
        >
          <Select
            options={[
              {
                value: '',
                label: placeholder('admin:venue.create.selectGovernorate', 'Select a governorate…'),
              },
              ...governorates,
            ]}
            data-testid="venue-create-governorate"
            {...govReg}
            onChange={(e) => {
              void govReg.onChange(e);
              setValue('cazaCode', '');
              setValue('cityCode', '');
            }}
          />
        </FormField>
        <FormField label={t('admin:venue.fields.caza')} error={errors.cazaCode?.message} required>
          <Select
            options={[
              {
                value: '',
                label: placeholder('admin:venue.create.selectCaza', 'Select a caza…'),
              },
              ...cazas,
            ]}
            disabled={!governorateIso}
            data-testid="venue-create-caza"
            {...cazaReg}
            onChange={(e) => {
              void cazaReg.onChange(e);
              setValue('cityCode', '');
            }}
          />
        </FormField>
        <FormField label={t('admin:venue.fields.city')} error={errors.cityCode?.message} required>
          <Select
            options={[
              {
                value: '',
                label: placeholder('admin:venue.create.selectCity', 'Select a city…'),
              },
              ...cities,
            ]}
            disabled={!cazaCode}
            data-testid="venue-create-city"
            {...register('cityCode')}
          />
        </FormField>
      </div>
      <FormField
        label={t('admin:venue.fields.streetAddress')}
        error={errors.streetAddress?.message}
      >
        <TextInput
          placeholder={t('admin:venue.fields.streetAddressPlaceholder')}
          {...register('streetAddress')}
        />
      </FormField>
      <div className="grid grid-cols-2 gap-4">
        <FormField label={t('admin:venue.fields.lat')} error={errors.lat?.message} required>
          <TextInput type="number" step="any" {...register('lat', { valueAsNumber: true })} />
        </FormField>
        <FormField label={t('admin:venue.fields.lng')} error={errors.lng?.message} required>
          <TextInput type="number" step="any" {...register('lng', { valueAsNumber: true })} />
        </FormField>
      </div>
      <div className="rounded-lg overflow-hidden border border-outline">
        <OsmMapPicker
          lat={Number.isFinite(watch('lat')) ? watch('lat') : null}
          lng={Number.isFinite(watch('lng')) ? watch('lng') : null}
          editable
          placeOnClick
          height={280}
          bbox={{ minLat: 33.05, maxLat: 34.7, minLng: 35.1, maxLng: 36.7 }}
          ariaLabel={t('admin:venue.f42.overview.locationMap', { defaultValue: 'Venue location' })}
          onChange={(coords) => {
            setValue('lat', coords.lat, { shouldValidate: true });
            setValue('lng', coords.lng, { shouldValidate: true });
          }}
        />
      </div>
      <div className="flex justify-between pt-2">
        <Button variant="ghost" type="button" onClick={onBack}>
          {t('admin:common.previous')}
        </Button>
        <Button type="submit" variant="primary">
          {t('admin:common.next')}
        </Button>
      </div>
    </form>
  );
}

function SportsStep({
  defaultValues,
  onNext,
  onBack,
}: {
  defaultValues: SportsValues;
  onNext: (values: SportsValues) => void;
  onBack: () => void;
}) {
  const { t } = useI18n();
  const [selected, setSelected] = useState<string[]>(defaultValues.sports);
  const [primary, setPrimary] = useState<string | undefined>(defaultValues.primarySport);
  const [touched, setTouched] = useState(false);

  const toggleSport = (sport: string) => {
    setSelected((prev) => {
      if (prev.includes(sport)) {
        const next = prev.filter((s) => s !== sport);
        if (primary === sport) setPrimary(next[0]);
        return next;
      }
      if (prev.length === 0) setPrimary(sport);
      return [...prev, sport];
    });
  };

  const handleNext = () => {
    setTouched(true);
    if (selected.length === 0) return;
    onNext({ sports: selected, primarySport: primary });
  };

  return (
    <div className="space-y-4">
      {touched && selected.length === 0 ? (
        <Banner tone="danger">{t('admin:venue.create.sportsRequired')}</Banner>
      ) : null}
      <div className="flex flex-wrap gap-2" data-testid="admin-venue-create-sports">
        {SPORT_NAME_VALUES.map((sport) => (
          <button
            key={sport}
            type="button"
            aria-pressed={selected.includes(sport)}
            data-sport={sport}
            onClick={() => toggleSport(sport)}
            className={[
              'inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-[12.5px] font-medium border transition-colors',
              selected.includes(sport)
                ? 'bg-accent text-accent-ink border-accent'
                : 'bg-bg text-ink-2 border-outline hover:border-accent',
            ].join(' ')}
          >
            <span
              aria-hidden="true"
              className="inline-flex size-7 shrink-0 [&_svg]:size-7 [&_svg]:shrink-0"
            >
              <SportIcon name={sport} size={28} />
            </span>
            {sport}
          </button>
        ))}
      </div>
      {selected.length > 0 ? (
        <div className="space-y-2">
          <p className="text-[12px] font-medium text-ink-2">
            {t('admin:venue.fields.primarySport')}
          </p>
          <div className="flex flex-wrap gap-2">
            {selected.map((sport) => (
              <Chip
                key={sport}
                selected={primary === sport}
                onClick={() => setPrimary(sport)}
                icon={
                  <span
                    aria-hidden="true"
                    className="inline-flex size-6 shrink-0 [&_svg]:size-6 [&_svg]:shrink-0"
                  >
                    <SportIcon name={sport as SportName} size={24} />
                  </span>
                }
              >
                {sport}
              </Chip>
            ))}
          </div>
        </div>
      ) : null}
      <div className="flex justify-between pt-2">
        <Button variant="ghost" type="button" onClick={onBack}>
          {t('admin:common.previous')}
        </Button>
        <Button variant="primary" type="button" onClick={handleNext}>
          {t('admin:common.next')}
        </Button>
      </div>
    </div>
  );
}

function HoursStep({
  defaultValues,
  onNext,
  onBack,
}: {
  defaultValues: DayTimeBandSelectorValue;
  onNext: (hours: DayTimeBandSelectorValue) => void;
  onBack: () => void;
}) {
  const { t } = useI18n();
  const [hours, setHours] = useState<DayTimeBandSelectorValue>(defaultValues);

  return (
    <div className="space-y-4">
      <DayTimeBandSelector value={hours} onChange={(v) => setHours(v)} />
      <div className="flex justify-between pt-2">
        <Button variant="ghost" type="button" onClick={onBack}>
          {t('admin:common.previous')}
        </Button>
        <Button variant="primary" type="button" onClick={() => onNext(hours)}>
          {t('admin:common.next')}
        </Button>
      </div>
    </div>
  );
}

function PhotosStep({
  onNext,
  onBack,
  photos,
  setPhotos,
}: {
  onNext: () => void;
  onBack: () => void;
  photos: FormState['photos'];
  setPhotos: (p: FormState['photos']) => void;
}) {
  const { t } = useI18n();

  return (
    <div className="space-y-4">
      <Banner tone="info">{t('admin:venue.create.photosBanner')}</Banner>
      <ImageUploader value={photos} onChange={(images) => setPhotos(images)} maxImages={10} />
      <div className="flex justify-between pt-2">
        <Button variant="ghost" type="button" onClick={onBack}>
          {t('admin:common.previous')}
        </Button>
        <Button variant="primary" type="button" onClick={onNext}>
          {t('admin:common.next')}
        </Button>
      </div>
    </div>
  );
}

function ReviewStep({
  formState,
  submitting,
  error,
  onBack,
  onSubmit,
}: {
  formState: Omit<FormState, 'photos'>;
  submitting: boolean;
  error: string | null;
  onBack: () => void;
  onSubmit: () => void;
}) {
  const { t } = useI18n();
  const { basics, sports } = formState;

  return (
    <div className="space-y-4">
      {error ? <Banner tone="danger">{error}</Banner> : null}
      <Card>
        <dl className="grid grid-cols-2 gap-3 text-[12.5px]">
          <div>
            <dt className="text-ink-3">{t('admin:venue.fields.name')}</dt>
            <dd className="text-ink font-medium">{basics.name}</dd>
          </div>
          <div>
            <dt className="text-ink-3">{t('admin:venue.fields.phone')}</dt>
            <dd className="text-ink">{basics.phone}</dd>
          </div>
          <div className="col-span-2">
            <dt className="text-ink-3">{t('admin:venue.fields.sports')}</dt>
            <dd className="text-ink flex flex-wrap gap-1 mt-1">
              {sports.sports.map((s) => (
                <Chip key={s} selected={s === sports.primarySport}>
                  {s}
                </Chip>
              ))}
            </dd>
          </div>
          {basics.description ? (
            <div className="col-span-2">
              <dt className="text-ink-3">{t('admin:venue.fields.description')}</dt>
              <dd className="text-ink">{basics.description}</dd>
            </div>
          ) : null}
        </dl>
      </Card>
      <div className="flex justify-between pt-2">
        <Button variant="ghost" type="button" onClick={onBack} disabled={submitting}>
          {t('admin:common.previous')}
        </Button>
        <Button variant="primary" type="button" onClick={onSubmit} loading={submitting}>
          {t('admin:common.submit')}
        </Button>
      </div>
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

export function AdminVenueCreatePage() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const toast = useToast();

  const [currentStep, setCurrentStep] = useState<Step>('basics');
  const [basics, setBasics] = useState<BasicsValues>(EMPTY_BASICS);
  const [location, setLocation] = useState<LocationValues>(EMPTY_LOCATION);
  const [sports, setSports] = useState<SportsValues>(EMPTY_SPORTS);
  const [hours, setHours] = useState(EMPTY_HOURS);
  const [photos, setPhotos] = useState<FormState['photos']>([]);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const stepIndex = STEP_INDEX[currentStep];

  /**
   * Upload + attach the step-5 photos to the freshly-created venue. Best-effort
   * per photo (a failed photo never voids the create) — returns the failure
   * count so the caller can surface a partial-success toast. (V-13 fix: the old
   * wizard silently discarded the Photos step.)
   */
  const uploadPhotos = async (venueId: string): Promise<number> => {
    let failures = 0;
    for (const image of photos) {
      try {
        const presigned = await adminVenueApi.getPhotoUploadUrl(venueId, {
          mime: image.file.type,
          sizeBytes: image.file.size,
          originalFilename: image.file.name,
        });
        const putRes = await fetch(presigned.url, {
          method: 'PUT',
          headers: presigned.headers as Record<string, string>,
          body: image.file,
        });
        if (!putRes.ok) throw new Error(`PUT ${putRes.status}`);
        await adminVenueApi.attachPhoto(venueId, {
          s3Key: presigned.s3Key,
          mime: image.file.type,
          sizeBytes: image.file.size,
        });
      } catch {
        failures += 1;
      }
    }
    return failures;
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    setSubmitError(null);
    try {
      const socialLinks = {
        ...(basics.website ? { website: basics.website } : {}),
        ...(basics.instagram ? { instagram: basics.instagram } : {}),
        ...(basics.facebook ? { facebook: basics.facebook } : {}),
        ...(basics.tiktok ? { tiktok: basics.tiktok } : {}),
      };
      // V-13 fix: the step-4 days × bands selection now maps onto openingHours.
      const openingHours = buildOpeningHours(hours);

      const venue = await adminVenueApi.createVenue({
        name: basics.name,
        phone: basics.phone,
        ...(basics.whatsappPhone ? { whatsappPhone: basics.whatsappPhone } : {}),
        ...(basics.description ? { description: basics.description } : {}),
        ...(Object.keys(socialLinks).length > 0 ? { socialLinks } : {}),
        ...(openingHours ? { openingHours } : {}),
        governorateIso: location.governorateIso,
        cazaCode: location.cazaCode,
        cityCode: location.cityCode,
        ...(location.streetAddress ? { streetAddress: location.streetAddress } : {}),
        lat: location.lat,
        lng: location.lng,
        sports: [...sports.sports] as SportName[],
        ...(sports.primarySport ? { primarySport: sports.primarySport as SportName } : {}),
      });

      // The venue exists from here on — photo problems must not surface as a
      // create failure (that's exactly the V-02 duplicate-venue trap).
      const photoFailures = photos.length > 0 ? await uploadPhotos(venue.id) : 0;
      if (photoFailures > 0) {
        toast.push({
          tone: 'warning',
          title: t('admin:venue.create.photoUploadPartial', {
            defaultValue:
              '{count, plural, one {# photo} other {# photos}} failed to upload — add them again from the venue page.',
            count: photoFailures,
          }),
        });
      }

      toast.push({ tone: 'success', title: t('admin:venue.create.success') });
      navigate(`/venues/${venue.id}`);
    } catch (err) {
      setSubmitError(translateApiError(err, t));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-6 max-w-2xl">
      <PageHeader title={t('admin:venue.pageTitle.create')} />

      <Stepper
        steps={STEPS.map((step) => ({ label: t(`admin:venue.create.steps.${step}`) }))}
        current={stepIndex}
      />

      {currentStep === 'basics' ? (
        <BasicsStep
          defaultValues={basics}
          onNext={(v) => {
            setBasics(v);
            setCurrentStep('location');
          }}
        />
      ) : currentStep === 'location' ? (
        <LocationStep
          defaultValues={location}
          onNext={(v) => {
            setLocation(v);
            setCurrentStep('sports');
          }}
          onBack={() => setCurrentStep('basics')}
        />
      ) : currentStep === 'sports' ? (
        <SportsStep
          defaultValues={sports}
          onNext={(v) => {
            setSports(v);
            setCurrentStep('hours');
          }}
          onBack={() => setCurrentStep('location')}
        />
      ) : currentStep === 'hours' ? (
        <HoursStep
          defaultValues={hours}
          onNext={(v) => {
            setHours(v);
            setCurrentStep('photos');
          }}
          onBack={() => setCurrentStep('sports')}
        />
      ) : currentStep === 'photos' ? (
        <PhotosStep
          photos={photos}
          setPhotos={(p) => setPhotos(p as FormState['photos'])}
          onNext={() => setCurrentStep('review')}
          onBack={() => setCurrentStep('hours')}
        />
      ) : (
        <ReviewStep
          formState={{ basics, location, sports, hours }}
          submitting={submitting}
          error={submitError}
          onBack={() => setCurrentStep('photos')}
          onSubmit={() => void handleSubmit()}
        />
      )}

      {submitting && currentStep === 'review' ? (
        <div className="flex justify-center py-4">
          <Spinner size={24} />
        </div>
      ) : null}
    </div>
  );
}
